import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listIncomingBookings, listMyRides } from '../lib/api';
import { useRouter } from '../lib/router';
import { useT } from '../lib/i18n';
import { useState } from 'react';
import { Card, CardHint, CardTitle } from '../components/Card';
import { RideCard } from '../components/RideCard';
import { RideCalendar } from '../components/RideCalendar';
import { RequestCard } from '../components/RequestCard';
import { ArchiveFolder } from '../components/ArchiveFolder';
import { RetryCard } from '../components/RetryCard';
import { PullIndicator } from '../components/PullIndicator';
import { usePullToRefresh } from '../lib/usePullToRefresh';
import { isBookingActive, isRideActive } from '../lib/lifecycle';

export function MyRidesPage(): JSX.Element {
  const { navigate } = useRouter();
  const { t } = useT();
  const [tab, setTab] = useState<'rides' | 'requests'>('rides');
  const qc = useQueryClient();
  const ptr = usePullToRefresh({
    onRefresh: () =>
      qc.invalidateQueries({ queryKey: tab === 'rides' ? ['my-rides'] : ['incoming-bookings'] }),
  });

  return (
    <div
      {...ptr.bind}
      style={{ transform: `translateY(${ptr.offset}px)`, transition: ptr.offset === 0 ? 'transform 200ms ease-out' : undefined }}
      className="relative mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6"
    >
      <PullIndicator offset={ptr.offset} refreshing={ptr.refreshing} />
      <header className="mb-3 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-tg-text">{t('my.driver.title')}</h1>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/my/templates')}
            className="inline-flex min-h-[44px] items-center rounded-input bg-tg-bg-secondary px-3 text-sm font-medium text-tg-text"
          >
            {t('home.s.templates.t')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/publish')}
            className="inline-flex min-h-[44px] items-center rounded-pill bg-brand-green px-4 text-sm font-semibold text-white"
          >
            {t('my.driver.publish')}
          </button>
        </div>
      </header>
      <Tabs tab={tab} setTab={setTab} />
      {tab === 'rides' ? <RidesTab /> : <RequestsTab />}
    </div>
  );
}

function Tabs({
  tab,
  setTab,
}: {
  tab: 'rides' | 'requests';
  setTab: (t: 'rides' | 'requests') => void;
}): JSX.Element {
  const { t } = useT();
  return (
    <div className="mb-4 grid grid-cols-2 gap-1 rounded-input bg-tg-bg-secondary p-1">
      {(['rides', 'requests'] as const).map((tk) => (
        <button
          key={tk}
          type="button"
          onClick={() => setTab(tk)}
          className={
            'rounded-input py-2 text-sm font-medium transition ' +
            (tab === tk ? 'bg-tg-bg text-tg-text shadow-sm' : 'text-tg-hint')
          }
        >
          {tk === 'rides' ? t('my.driver.tabRides') : t('my.driver.tabRequests')}
        </button>
      ))}
    </div>
  );
}

function RidesTab(): JSX.Element {
  const { navigate } = useRouter();
  const { t } = useT();
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const q = useQuery({ queryKey: ['my-rides'], queryFn: listMyRides });
  if (q.isLoading) return <Skeleton />;
  if (q.isError) return <RetryCard message={(q.error as Error).message} onRetry={() => q.refetch()} isRetrying={q.isFetching} />;

  const items = q.data?.items ?? [];
  const active = items.filter((r) => isRideActive(r.status));
  const archivedCount = items.length - active.length;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-1 rounded-input bg-tg-bg-secondary p-1">
        {(['list', 'calendar'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={
              'rounded-input py-1.5 text-xs font-medium transition ' +
              (view === v ? 'bg-tg-bg text-tg-text shadow-sm' : 'text-tg-hint')
            }
          >
            {v === 'list' ? t('my.driver.tabList') : t('calendar.tab')}
          </button>
        ))}
      </div>
      {view === 'list' ? (
        <>
          {active.length > 0 ? (
            active.map((r) => (
              <RideCard key={r.id} ride={r} hideDriver onOpen={() => navigate(`/ride/${r.id}`)} />
            ))
          ) : (
            <Card className="space-y-3 text-center">
              <CardTitle>{t('my.driver.noRides.t')}</CardTitle>
              <CardHint>{t('my.driver.noRides.b')}</CardHint>
              <button
                type="button"
                onClick={() => navigate('/publish')}
                className="mx-auto inline-flex min-h-[44px] items-center rounded-pill bg-brand-green px-4 text-sm font-semibold text-white"
              >
                {t('my.driver.publish')}
              </button>
            </Card>
          )}
          {archivedCount > 0 && (
            <ArchiveFolder
              label={t('my.driver.pastRides')}
              count={archivedCount}
              onClick={() => navigate('/my/rides/archive?tab=rides')}
            />
          )}
        </>
      ) : (
        // Calendar is time-organized, so showing all rides there is fine.
        <RideCalendar rides={items} onOpenRide={(id) => navigate(`/ride/${id}`)} />
      )}
    </div>
  );
}

function RequestsTab(): JSX.Element {
  const { navigate } = useRouter();
  const { t } = useT();
  const q = useQuery({ queryKey: ['incoming-bookings'], queryFn: listIncomingBookings });
  if (q.isLoading) return <Skeleton />;
  if (q.isError) return <RetryCard message={(q.error as Error).message} onRetry={() => q.refetch()} isRetrying={q.isFetching} />;

  const items = q.data?.items ?? [];
  const active = items.filter((b) => isBookingActive(b.status));
  const archivedCount = items.length - active.length;

  return (
    <div className="space-y-3">
      {active.length > 0 ? (
        active.map((b) => (
          <RequestCard key={b.id} booking={b} onOpen={() => navigate(`/booking/${b.id}`)} />
        ))
      ) : (
        <Card className="text-center">
          <CardTitle>{t('my.driver.noRequests.t')}</CardTitle>
          <CardHint>{t('my.driver.noRequests.b')}</CardHint>
        </Card>
      )}
      {archivedCount > 0 && (
        <ArchiveFolder
          label={t('my.driver.resolvedRequests')}
          count={archivedCount}
          onClick={() => navigate('/my/rides/archive?tab=requests')}
        />
      )}
    </div>
  );
}

function Skeleton(): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
      <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
      <div className="h-24 animate-pulse rounded-card bg-tg-bg-secondary" />
    </div>
  );
}
